/**
 * Les trois sons du Fil — RG-2.42 à RG-2.44, §392.
 *
 * ── SYNTHÉTISÉS, JAMAIS TÉLÉCHARGÉS ────────────────────────────────────────
 *
 * Un oscillateur et une enveloppe. Aucun fichier : un candidat haïtien paie sa
 * donnée, le mode hors-ligne précharge cinquante questions, et un son absent
 * parce que le réseau a lâché serait un défaut de plus à diagnostiquer.
 *
 * ── LE SON D'UNE ERREUR NE PUNIT PAS ───────────────────────────────────────
 *
 * RG-P50 : « une réponse fausse ne retire rien ; le candidat qui se trompe a
 * travaillé autant que l'autre ». Un buzzer apprend à ne plus répondre. Celui-ci
 * dit « noté », pas « raté » : une note grave, douce, sans dissonance.
 */

/** Actif par défaut — Ben les demande, ils doivent s'entendre. */
const CLE = 'upfac.son';

/**
 * Un seul contexte pour toute la session, créé au PREMIER son.
 *
 * Les navigateurs refusent de démarrer l'audio hors d'un geste de
 * l'utilisateur. Le créer au chargement le laisserait `suspended` pour
 * toujours ; le créer quand une réponse vient d'être envoyée le crée pendant le
 * geste, et il démarre.
 */
let contexte: AudioContext | null = null

function leContexte(): AudioContext | null {
  if (contexte !== null) {
    return contexte
  }

  // `webkitAudioContext` pour les Safari anciens, encore nombreux sur les
  // téléphones d'occasion.
  const Constructeur =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

  if (Constructeur === undefined) {
    return null
  }

  try {
    contexte = new Constructeur()
  } catch {
    // Un navigateur qui refuse l'audio ne doit pas casser la réponse.
    return null
  }

  return contexte
}

/** Le son est-il actif sur CET appareil ? — RG-2.44. */
export function sonActif(): boolean {
  try {
    return globalThis.localStorage?.getItem(CLE) !== 'off'
  } catch {
    // Navigation privée, stockage refusé : on préfère le son au silence.
    return true
  }
}

/** L'audio a-t-il déjà été ouvert par un geste ? */
let ouvert = false

/**
 * Ouvrir l'audio PENDANT un geste — la seule fenêtre que les téléphones laissent.
 *
 * ── LE VRAI MOTIF DU SILENCE ───────────────────────────────────────────────
 *
 * Ben, 9 septembre 2026 : « le son n'est toujours pas audible en prod ». Le
 * volume n'était que la moitié du problème.
 *
 * Le contexte se créait au PREMIER SON, c'est-à-dire dans la réponse du serveur
 * — une promesse, jamais un geste. Les navigateurs de téléphone y créent le
 * contexte **suspendu**, et un `resume()` appelé hors geste y est ignoré : les
 * notes partaient sur une horloge gelée, à n'importe quel volume.
 *
 * **iOS demande davantage qu'un `resume()`** : il faut qu'un son ait été JOUÉ
 * pendant le geste. Un tampon d'un seul échantillon, muet, suffit — c'est
 * l'idiome connu, et il ne s'entend pas.
 *
 * **Appelée à chaque geste, elle ne travaille qu'une fois.** Le déclencheur est
 * posé sur toute la page ; sans garde, chaque clic rejouerait un tampon.
 */
export function preparerLeSon(): void {
  if (ouvert) {
    return
  }

  const ctx = leContexte()

  if (ctx === null) {
    return
  }

  ouvert = true

  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => undefined)
  }

  try {
    const muet = ctx.createBuffer(1, 1, ctx.sampleRate)
    const source = ctx.createBufferSource()
    source.buffer = muet
    source.connect(ctx.destination)
    source.start(0)
  } catch {
    // Un navigateur qui refuse le tampon garde le `resume()` : c'est déjà ce
    // qu'il faut sur les navigateurs de bureau.
  }
}

export function reglerLeSon(actif: boolean): void {
  try {
    globalThis.localStorage?.setItem(CLE, actif ? 'on' : 'off')
  } catch {
    // Rien à faire : le réglage ne survivra pas à la page, et c'est tout.
  }
}

/**
 * Une note, avec une enveloppe qui monte et redescend.
 *
 * **L'enveloppe n'est pas un ornement** : une onde qu'on démarre et arrête
 * brutalement produit un clic, audible et désagréable. La rampe de gain
 * l'efface.
 */
function note(
  ctx: AudioContext,
  hz: number,
  debut: number,
  duree: number,
  volume: number,
  timbre: OscillatorType,
): void {
  const oscillateur = ctx.createOscillator()
  const gain = ctx.createGain()

  oscillateur.type = timbre
  oscillateur.frequency.setValueAtTime(hz, ctx.currentTime + debut)

  gain.gain.setValueAtTime(0, ctx.currentTime + debut)
  gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + debut + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + debut + duree)

  oscillateur.connect(gain)
  gain.connect(ctx.destination)

  oscillateur.start(ctx.currentTime + debut)
  oscillateur.stop(ctx.currentTime + debut + duree + 0.02)
}

/**
 * @param notes [fréquence, retard, durée]
 * @param timbre `triangle` porte plus loin qu'une sinusoïde et sonne plus gai
 *               sans devenir agressif ; `sine` reste pour ce qui doit rester
 *               discret.
 */
function jouer(
  notes: readonly (readonly [number, number, number])[],
  volume: number,
  timbre: OscillatorType = 'sine',
): void {
  if (!sonActif()) {
    return
  }

  const ctx = leContexte()

  if (ctx === null) {
    return
  }

  const poser = (): void => {
    for (const [hz, debut, duree] of notes) {
      note(ctx, hz, debut, duree, volume, timbre)
    }
  }

  // ── UNE HORLOGE SUSPENDUE NE JOUE RIEN ──────────────────────────────────
  //
  // **C'est le motif du silence que Ben a signalé trois fois.** La version
  // précédente appelait `resume()` puis posait aussitôt les notes à
  // `currentTime + retard`. Sur un contexte suspendu, `currentTime` NE BOUGE
  // PAS : le réveil est asynchrone, et quand il arrive, les instants calculés
  // sont déjà derrière. Les oscillateurs démarraient dans le vide.
  //
  // Les notes attendent donc le réveil. Le retard est imperceptible — un
  // contexte se réveille en quelques millisecondes —, et c'est de toute façon
  // le seul cas où l'alternative est le silence.
  if (ctx.state === 'suspended') {
    void ctx
      .resume()
      .then(poser)
      // Un navigateur qui refuse le réveil ne doit pas casser la réponse ; on
      // tente quand même, au cas où l'état ait menti.
      .catch(() => {
        poser()
      })

    return
  }

  poser()
}

/**
 * L'accord parfait majeur, monté puis TENU — RG-2.42.
 *
 * **Ben, le 8 septembre 2026 : « le son doit être plus gai et durable ».** La
 * version précédente tenait 0,2 seconde : deux notes en sinusoïde, si brèves
 * qu'elles passaient pour un clic. Elle récompensait sans qu'on l'entende.
 *
 * Trois changements, et chacun répond à un mot :
 *
 * · **GAI** — do-mi-sol-do, l'accord parfait majeur, là où la quinte seule
 *   restait ambiguë. Et le timbre passe en `triangle`, plus riche en
 *   harmoniques qu'une sinusoïde, donc plus clair sur un haut-parleur de
 *   téléphone — sans l'agressivité d'une onde carrée.
 * · **DURABLE** — 0,58 seconde au lieu de 0,20, et la dernière note tient à
 *   elle seule un tiers du total : c'est elle qui reste dans l'oreille.
 * · **AUDIBLE** — les notes se CHEVAUCHENT au lieu de se suivre, ce qui les
 *   fait entendre comme un accord et non comme quatre bips.
 *
 * ── LE VOLUME, REPRIS LE 9 SEPTEMBRE ───────────────────────────────────────
 *
 * **Ben, le lendemain : « je n'entends plus de sons ».** Le son était devenu
 * plus gai et trois fois plus long — et 21 % plus fort seulement, de 0,07 à
 * 0,085. Rendu hors ligne, il culmine à 0,087 d'amplitude : sur le
 * haut-parleur d'un téléphone, dans une pièce, cela ne s'entend pas.
 *
 * **0,30, soit un peu plus de 12 dB de plus.** C'est net sans être agressif :
 * la montée de 12 ms empêche toujours le clic, et la descente exponentielle
 * évite la coupure sèche.
 *
 * **Le volume est désormais MESURÉ par un test.** La durée l'était, la hauteur
 * l'était ; le volume, non — et c'est précisément par là que le son est devenu
 * inaudible sans que rien ne rougisse.
 */
export function sonJuste(): void {
  jouer(
    [
      [523, 0, 0.16],
      [659, 0.075, 0.18],
      [784, 0.15, 0.22],
      [1047, 0.225, 0.36],
    ],
    0.3,
    'triangle',
  )
}

/**
 * Deux notes graves qui se posent — RG-2.43.
 *
 * **Ben, le 8 septembre 2026 : « il faut un son pour les ratés ».** Il en
 * existait un — une note à 196 Hz, 0,19 seconde, volume 0,05 — et personne ne
 * l'entendait. Un son qu'on n'entend pas n'existe pas.
 *
 * **Il devient audible sans devenir un buzzer.** RG-P50 tient : « une réponse
 * fausse ne retire rien ; le candidat qui se trompe a travaillé autant que
 * l'autre ». D'où une descente d'un TON — do vers si bémol —, consonante,
 * lente, en sinusoïde. Elle dit « noté », jamais « raté ». Ce qu'on a écarté :
 * la seconde mineure et le triton, qui sonnent l'erreur ; la descente de
 * quinte, qui sonne l'échec ; et toute onde carrée, qui sonne l'alarme.
 *
 * **Le volume monte aussi, le 9 septembre, et pour la même raison.** 0,065 ne
 * s'entendait pas davantage que 0,085. Il passe à **0,26** — un peu en deçà de
 * la bonne réponse, parce qu'un raté n'a pas à s'imposer plus fort qu'une
 * réussite, mais bien au-dessus du seuil où un son existe sans qu'on l'entende.
 */
export function sonFaux(): void {
  jouer(
    [
      [262, 0, 0.18],
      [233, 0.12, 0.32],
    ],
    0.26,
  )
}

/** Trois notes qui montent — l'accord parfait, un peu plus tenu. */
export function sonObjectifAtteint(): void {
  jouer(
    [
      [523, 0, 0.13],
      [659, 0.1, 0.13],
      [784, 0.2, 0.28],
    ],
    0.08,
  )
}

/** Pour les tests : oublie le contexte retenu. */
export function oublierLeContexte(): void {
  contexte = null
  // Le drapeau d'ouverture appartient au contexte : l'oublier sans lui ferait
  // croire au suivant qu'un geste l'a déjà ouvert.
  ouvert = false
}
